<template>
  <div @mousedown.stop @mouseup.stop @dblclick.stop @click.stop>

    <!-- ELEMENT HIGHLIGHTER -->
    <template v-if="mounted" v-for="(definitions, type) in selectors">
        <Highlighter v-for="definition in definitions" :key="definition.s + (definition.c || '')"
                :window="window" :changes="changes" :type="type" :definition="definition" 
                :selected="selected.indexOf(definition) !== -1" :active="active === definition" :hover="hover === definition"
                @click="tooltipToggle" style="display:none !important" />
    </template>

    <!-- MENU -->
    <ul v-if="active" ref="menu" :class="{['jbo-popover--active']: menu }" class="jbo-popover">
      <li v-for="index in [1, 2]" :key="index"
          @click.stop="setStep(index)"
          class="selector-popover-item__default"
          :class="[{ selected: isSelected(index) }, 'step-' + [index]]">
        Step {{ index }}
      </li>
      <li @click.stop="selectSimilar" class="selector-popover-item__default" :class="{ selected: hasGroup, ['group-edit']: true }">
        {{ findSimilarText }}
      </li>
      <li class="popper__arrow"></li>
    </ul>
  </div>
</template>

<script>
const JBO_POPPER_MARGIN       = 10
const JBO_TOOLTIP_SIZE        = 2

import $ from 'jquery';
import Popper from 'popper.js';
import Vue from 'vue';

import Proxy from '../../mixins/Proxy'
import Highlighter from './Highlighter.vue'
import CssSelectorGenerator from '../../static/js/css-selector-generator.js'

const debounce = require('debounce');
const CssSelector = new CssSelectorGenerator({ modifier: Proxy.methods.modifySelectorForProxy })
const LABELS    = ['product_image', 'campaign_name', 'shipping_price', 'redirect_link']

export default {
  name          : 'Editor',
  components    : { Highlighter },
  props         : { window: null },
  mixins        : [ Proxy ],

  data() {
    return {
      mounted                   : false,
      mousemove                 : { x: 0, y: 0 },
      menu                      : null,
      group                     : [],
      hover                     : null,
      active                    : null,
      interval                  : null,
      debounceEvent             : debounce(() => this.pendingChanges.push(this.window), 100),
      pendingChanges            : [],
      changes                   : [],
    }
  },

  computed: {
    document() {
      return this.window.document
    },
    mousePosition() {
      return {
        x: this.mousemove.pageX - $(this.window).scrollLeft(),
        y: this.mousemove.pageY - $(this.window).scrollTop()
      }
    },
    selectors() {
      const selectors = { ... this.$store.getters.getSelection() }
      const type      = LABELS[this.$store.state.rotator.activeType]

      // Add any selected elements (no duplicates)
      if (type)
        selectors[type] = $.unique(selectors[type].concat(this.selected).concat(this.hover? [this.hover]: []))

      return selectors
    },
    activeSelectors() {
      return this.selectors[LABELS[this.$store.state.rotator.activeType]]
    },
    selected() {
      return (!this.active || this.group.indexOf(this.active) !== -1)
        ? this.group
        : this.group.concat([ this.active ])
    },
    hasGroup() {
      return this.selected.length > 1
    },
    findSimilarText() {
      return this.hasGroup? 'Unselect elements': 'Find more like this'
    },
    textSelection() {
      return this.window.getSelection? this.window.getSelection(true): this.document.selection
    }
  },

  mounted() {
    // Inject our CSS styles
    const iframeStyleTag    = this.document.createElement('link')
    iframeStyleTag.href     = '//' + window.location.hostname + ':' + window.location.port + '/static/css/iframe.css'
    iframeStyleTag.rel      = 'stylesheet'
    this.document.head.appendChild(iframeStyleTag)
    
    // Convert anchors so that they're not clickable
    this.convertAnchorTags()
    
    // Watch for dom changes to update highlightors
    this.observeDomChanges((records) => {
      records.forEach((mutation) => {
        // Ignore our own mutation events (to the best of our ability.. some will slip by this check)
        if (!mutation.target || (mutation.target.className || '').indexOf('jbo-') !== -1)
          return
        
        // Keep track of added and removed nodes, so we can re-draw highlighters
        if (mutation.removedNodes && mutation.removedNodes.length > 0)
          this.pendingChanges.push(...mutation.removedNodes)
        if (mutation.addedNodes && mutation.addedNodes.length > 0)
          this.pendingChanges.push(...mutation.addedNodes)
        
        // Ignore style and attribute changes for now or we may end up in some endless loops
        this.pendingChanges.push(mutation.target)
      })
    })
    this.window.addEventListener('resize', this.debounceEvent, true)
    this.window.addEventListener('scroll', this.debounceEvent, true)
    this.interval = setInterval(this.handleDomUpdates, 100)
    setInterval(this.debounceEvent, 1000)
    
    // Keep track of mouse positioning
    $(this.document).mousemove((ev) => this.mousemove = ev)
    
    // Mount editor & finish initialization
    this.document.body.appendChild(this.$el)
    this.changeActiveType(this.$store.state.rotator.activeType)
    this.mounted = true
  },

  destroyed() {
    this.mounted = false
    this.window.removeEventListener('resize', this.debounceEvent)
    this.window.removeEventListener('scroll', this.debounceEvent)
    clearInterval(this.interval)
  },

  watch: {
    '$store.state.rotator.activeType'(newSelectorIndex, oldSelectorIndex) {
      this.changeActiveType(newSelectorIndex, oldSelectorIndex)
    }
  },

  methods: {
    changeActiveType(newSelectorIndex, oldSelectorIndex) {
      const PRODUCT_QUERY   = {
        0: 'img, .jbo-selector-product_image.jbo-overlay',
        3: '.anchor-to-span, .jbo-selector-redirect_link.jbo-overlay'
      }
      const EL_LABEL        = ['product_image', 'campaign_name', 'shipping_price', 'redirect_link']

      this.clearSelection(true)

      // Clear old events
      $('html', this.document)
          .off('mouseenter mouseleave', this.setHoverElement)
          .off('click', this.clearSelection)
          .off('mouseup keyup', this.setTextSelection)
          .find('body')
            .removeClass('jbo-tab-'+EL_LABEL[oldSelectorIndex])
            .addClass('jbo jbo-tab-'+EL_LABEL[newSelectorIndex])

      // Events for element selectors
      if (newSelectorIndex == 0 || newSelectorIndex == 3) {
        $('html', this.document)
            .on('mouseenter mouseleave', PRODUCT_QUERY[newSelectorIndex], this.setHoverElement)
            .on('click', this.clearSelection)
            .find('body')
              .addClass('jbo-text-noselect')

      // Events for text selectors
      } else if (newSelectorIndex == 1 || newSelectorIndex == 2) {
        $('html', this.document)
            .on('mouseup keyup', this.setTextSelection)
            .find('body')
              .removeClass('jbo-text-noselect')
      }
    },
    
    convertAnchorTags() {
      // Convert anchor tags to spans
      $('a', this.document).each((index, element) => {
        const $element        = $(element)
        const span            = this.document.createElement('span')
      
        // Preserve original URL
        span.setAttribute('data-jbo-href', $element.attr('href'))
      
        // Copy over attributes
        Object.values(element.attributes).forEach((attr) => {
          try {
            span.setAttribute(attr.name, attr.value)
          } catch (err) {}
        })
      
        // Copy over style
        span.style.cssText    = this.window.getComputedStyle(element, null).cssText
        span.className        = 'anchor-to-span ' + element.className

        // Style for all children (css might not work since selectors change)
        $.each($element.find('*:not(a):not(img)'), (index, element) => {
          element.style.cssText = this.window.getComputedStyle(element, null).cssText
        })

        // Copy over events
        const events          = $element.data('events') || {}
        Object.keys(events).forEach((eventType) => {
          events[eventType].forEach((event) => {
            const eventToBind = event.namespace.length
              ? event.type + '.' + event.namespace
              : event.type
      
            span.bind(eventToBind, event.data, event.handler)
          })
        })
      
        // Clone children and replace
        $element.contents().appendTo(span)
        $element.replaceWith(span)
      })
    },

    findLargeIframes() {
      const $window  = $(this.window, this.document)
      const threshold= ($window.width() * $window.height()) / 2

      $('iframe:not(.jbo-iframe)', this.document).each((index, element) => {
        const $element = $(element).addClass('jbo-iframe')
        
        // If iframe is sized with a % height, convert it to vh which is a more window-independent unit
        if (/%/.test($element.css('height')))
          $element.css({ height: parseFloat($element.css('height')) + 'vh' })

        const area     = $element.outerHeight() * $element.outerWidth()
        const msg      = "It appears like this page is serving iFramed content.<br/>" +
                         "You must integrate the URL in the iFrame instead."

        if (area > threshold && $element.is(':visible')) {
          this.$alert(msg, 'Oops!', {
            showClose: false,
            confirmButtonText: 'OK',
            dangerouslyUseHTMLString: true
          }).then(() => {
            this.$store.dispatch('setOverrideUrl', $element.attr('src'))
          })
        }
      })
    },
    
    observeDomChanges(callback) {
      if (this.window.MutationObserver) {
        const observer = new MutationObserver(callback)
        observer.observe(this.document.body, { childList:true, subtree:true, characterData:true, attributes:true });
      }
    },
    
    handleDomUpdates() {
      this.changes        = this.pendingChanges
      this.pendingChanges = []
      
      this.convertAnchorTags()
      this.findLargeIframes()
    },

    isSelected(step) {
      return !this.selected.some((definition) => definition.i != step)
    },

    setStep(step) {
      const payloads    = []
      const isSelected  = this.isSelected(step)

      this.selected.forEach((definition) => {
        if (isSelected || definition.i != step)
          payloads.push({ ...definition, i: step, type: this.$store.state.rotator.activeType })
      })

      !isSelected
        ? this.$store.dispatch('addSelection', payloads)
        : this.$store.dispatch('removeSelection', payloads)

      this.clearSelection()
    },

    selectSimilar() {
      this.hasGroup
        ? this.group = []
        : this.findSimilar()
    },

    findSimilar() {
      const $active         = $(this.modifySelectorForProxy(this.active.s), this.document)
      const existingNodes   = this.activeSelectors
      let nodes             = []

      switch(this.$store.state.rotator.activeType) {
      case 0:
        nodes = $.makeArray($('[src="' + this.escapeQuotes($active.attr('src') || ' ') + '"]', this.document))
        break;

      case 1:
      case 2:
        var node,
          regex = new RegExp(this.active.c, 'gim'),
          walk  = document.createTreeWalker(this.document.body, NodeFilter.SHOW_TEXT, null, false)

        while((node = walk.nextNode()) != null)
          if (node.parentNode && node.parentNode.nodeName !== 'SCRIPT' && regex.test(node.textContent || node.innerText))
            nodes.push(node.parentNode.nodeName == 'MARK'? node.parentNode.parentNode: node.parentNode)

        break

      case 3:
        nodes = $.makeArray($('[data-jbo-href="' + this.escapeQuotes($active.attr('data-jbo-href') || '') + '"]', this.document))
        break;
      }

      this.group        = []
      nodes             = $.unique(nodes)
      const numNewNodes = nodes.filter((el) => {
        if (el === $active[0] || $(el, this.document).parents('.jbo-popover').length)
          return false

        const selector  = CssSelector.getSelector(el)
        const existing  = existingNodes.find((defn) => defn.s == selector && defn.c == this.active.c)
        return this.group.push(existing || { ...this.active, s: selector, i: null })
      }).length

      this.$message.closeAll()
      return this.$message({
        type          : numNewNodes? 'success': 'warning',
        message       : numNewNodes? numNewNodes + ' similar element(s) found': 'No similar elements found',
        center        : true,
        customClass   : 'jbo-message'
      });
    },

    trimTrailingSlash(str) {
      return str.replace(/(\/+|\s+)(\?|#|$)/, '')
    },
    escapeQuotes(str) {
      return str.replace(/\"/g,'\\"')
    },
    // Fuzzy regex for matching ignoring whitespace
    fuzzyRegExp(str) {
      return str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&').replace(/\s+/g, '\\s*')
    },
    clearSelection(clearTextSelection = false) {
      if (clearTextSelection)
        this.clearTextSelection()

      if (this.menu)
        this.menu.destroy()

      this.group    = []
      this.active   = this.hover = this.menu = null
    },
    clearTextSelection() {
      const selection = this.textSelection
      selection.removeAllRanges? selection.removeAllRanges(): selection.empty()
    },
    setHoverElement(ev) {
      ev.stopImmediatePropagation()

      // Types of events to ignore
      if (ev.buttons & 2 ||
          (ev.type == 'mouseleave' && $(ev.relatedTarget).is('.jbo-overlay') && ev.relatedTarget.jbo_definition == this.hover) ||
          (ev.type == 'mouseenter' && $(ev.currentTarget).is('.jbo-overlay'))
      ) return

      const selector = CssSelector.getSelector(ev.currentTarget)
      this.hover = (ev.type == 'mouseenter')
        ? (this.activeSelectors.find((definition) => definition.s == selector) || { s: selector })
        : null;
    },

    isValidTextSelection(content) {
      return content && (content.length >= 3 || /\d/.test(content))
    },

    selectedText() {
      return this.textSelection && this.textSelection.toString
        ? this.textSelection.toString()
        : this.textSelection.createRange().text
    },

    setTextSelection() {
      this.clearSelection()

      const selection = this.textSelection
      if (selection.anchorNode == undefined)
        return

      // Flag for whether or not the user did the text selection in a forward direction ( |---> ) vs ( <----| )
      const isReverse   = selection.anchorNode.compareDocumentPosition
        ? (selection.anchorNode.compareDocumentPosition(selection.focusNode) & 2) > 0
        : selection.anchorNode.sourceIndex > selection.focusNode.sourceIndex
      const siblingType = !isReverse? 'nextSibling': 'previousSibling'

      let currentNode   = selection.anchorNode
      let textNode, startIndex, endIndex, content, parentNode

      // Iterate the selection tree to find the first valid text selection window
      do {
        // Find the first suitable text node
        textNode    = currentNode.nodeType === 3
          ? currentNode
          : (!isReverse? Array.from(currentNode.childNodes): Array.from(currentNode.childNodes).reverse()).find((el) => {
            return el.nodeType === 3 && this.isValidTextSelection(el.nodeValue.trim()) && (currentNode = el)
          })

        // Ensure that a text node exists
        if (textNode && ['MARK', 'SCRIPT'].indexOf((currentNode.tagName || currentNode.parentElement.tagName)) === -1) {

          startIndex  = textNode === selection.anchorNode
            ? selection.anchorOffset
            : (!isReverse? 0: textNode.nodeValue.length)
          endIndex    = textNode === selection.focusNode
            ? selection.focusOffset
            : (!isReverse? textNode.nodeValue.length: 0)

          content     = textNode.nodeValue.slice(
            Math.min(startIndex, endIndex), Math.max(startIndex, endIndex)
          ).trim().toLowerCase()
        }

        // Continue traversing the tree
        parentNode  = currentNode.parentElement
        currentNode = currentNode != selection.focusNode && textNode != selection.focusNode
          ? currentNode[siblingType] || currentNode.parentNode[siblingType] || selection.focusNode
          : null

      } while (currentNode && !this.isValidTextSelection(content))

      // Show a warning if text selection spanned multiple nodes
      if (content && (!this.selectedText() || this.selectedText().trim().toLowerCase() != content)) {
        this.$message.closeAll()
        this.$message({
          type          : 'warning',
          message       : 'Text selection has been reduced to a single HTML node.',
          center        : true,
          customClass   : 'jbo-message'
        });
      }

      selection.empty()

      // If successful, selection becomes active
      if (this.isValidTextSelection(content))
        this.active = { s: CssSelector.getSelector(parentNode), c: this.fuzzyRegExp(content) }
    },

    distance(rect, p) {
      // Distance will be 0 if we're inside the bounding box
      if ((rect.left <= p.x && rect.right >= p.x && rect.top <= p.y && rect.bottom >= p.y))
        return 0

      // Otherwise find the distance to the closest vertex or center
      const dx = Math.min(Math.abs(p.x - rect.left), Math.abs(p.x - rect.right))
      const dy = Math.min(Math.abs(p.y - rect.top), Math.abs(p.y - rect.bottom))
      return Math.sqrt(dx*dx + dy*dy);
    },

    tooltipToggle(highlighter, $targets) {
      const selected    = this.selected
      this.clearSelection(true)
      this.active       = highlighter.definition

      // If click element is not in the current selection, we need to clear out the selection
      if (selected.indexOf(this.active) !== -1)
        this.group = selected

      // Find the closest target to the mouse position
      const mousePosition = this.mousePosition
      let selectedElement, minDist = 9999999999
      $targets.each((i, el) => {
        const dist = this.distance(el.getBoundingClientRect(), mousePosition)
        if (dist < minDist) {
          minDist           = dist
          selectedElement   = el
        }
      })

      this.$nextTick(() => {
        let placement = 'top'
        this.menu = new Popper(selectedElement, this.$refs.menu, {
          placement: placement,
          modifiers: {
            preventOverflow: {
              escapeWithReference: true,
            },
            //Properly reposition the Popper when inline element wraps multiple lines
            repositionForInlineElements: {
              enabled : true,
              order   : 400,
              fn      : (data) => {
                try {
                  const { popper, reference }   = data.offsets
                  const offset                  = selectedElement.getBoundingClientRect()
                  const boxes                   = Array.from(selectedElement.getClientRects())
                  const JBO_POPPER_MARGIN       = 10
                  const JBO_TOOLTIP_SIZE        = 2
                  placement                     = data.placement

                  boxes.sort((a, b) => {
                    return this.distance(a, mousePosition) - this.distance(b, mousePosition)
                  })
                  const truePosition = boxes[0]

                  popper[data.placement]   += Math.floor(truePosition[data.placement] - offset[data.placement])
                  popper.left              += Math.floor((truePosition.left + (truePosition.width / 2)) - (offset.left + (offset.width / 2)))

                  reference[data.placement]+= Math.floor(truePosition[data.placement] - offset[data.placement])
                  reference.height          = truePosition.height

                  // Popper will fall off the left of the page
                  if (popper.left < JBO_POPPER_MARGIN) {
                    reference[data.placement] -= (reference.top + (reference.height / 2)) - (popper.top + (popper.height / 2))
                    popper.left                = (truePosition.right + JBO_TOOLTIP_SIZE)
                    placement = 'left'
                  // Popper will fall off the right of the page
                  } else if ((popper.left + popper.width + JBO_POPPER_MARGIN) > this.window.innerWidth) {
                    reference[data.placement] -= (reference.top + (reference.height / 2)) - (popper.top + (popper.height / 2))
                    popper.left                = (truePosition.left - popper.width - JBO_TOOLTIP_SIZE)
                    placement                  = 'right'
                  }

                  return data
                } catch (err) {
                  this.menu && this.menu.destroy()
                }
              }
            },
            repositionTooltipPlacement: {
              enabled : true,
              order   : 900,
              fn      : (data) => {
                this.$refs.menu.setAttribute('x-placement', placement)
              }
            }
          }
        })
      })
    }
  }
}
</script>

<style type="scss">
.jbo-message {
  top: 60px;
  z-index: 99 !important;
}
</style>



// WEBPACK FOOTER //
// Editor.vue?0d509590